<script setup lang="ts">
import { onBeforeMount, ref } from "vue";
import { useI18n } from "vue-i18n";

import { getContractTypeOptions } from "@/helpers/filterOptions";
import { isset } from "@/helpers/object";
import { useConfigurationStore } from "@/store/configurationStore";
import { EContractType } from '@/types/contract';

import type { supporttedEventNames } from "@/dsComponents/Dropdown/types";
import type { LabelValue } from '@/types/common';

import ComboButton from "@/dsComponents/buttons/ComboButton.vue";
import { DropdownItem } from "@/dsComponents/Dropdown";
import ControlledDropdown from "@/dsComponents/Dropdown/ControlledDropdown.vue";

interface ContractsDropdownProps {
    modelValue: LabelValue | null;
    placeholder?: string;
    icon?: string;
    error?: boolean;
    width?: string;
    showIcon?: boolean;
    fitContent?: boolean;
    fullWidth?: boolean;
    useApiSource?: boolean;
    variant?: "ghost" | "stroke";
}

const props = withDefaults(defineProps<ContractsDropdownProps>(), {
    icon: "signature",
    width: "380px",
    showIcon: true,
    fitContent: true,
    fullWidth: false,
    useApiSource: false,
    variant: "ghost",
});

const emit = defineEmits<(event: "update:modelValue", value: LabelValue | null) => void>();

const { t } = useI18n();
const configStore = useConfigurationStore();

const contracts = ref<LabelValue[]>([]);
const dropdownContract = ref<LabelValue[]>([]);

const open = ref(false);

const toggle = () => {
    open.value = !open.value;
};

const handleControlledDropdownChange = (event: supporttedEventNames) => {
    if (event === "clickaway") open.value = false;
};

const handleContractSelect = (contract: string) => {
    emit("update:modelValue", contracts.value.find((j: LabelValue) => j.value === contract) ?? null);
    open.value = false;
};

onBeforeMount(() => {
    const contractOptions = props.useApiSource
        ? configStore.jobContractOptions
        : getContractTypeOptions()
              .filter((ct: LabelValue) => isset(ct.label))
              .filter((ct: LabelValue) => ct.value !== EContractType.NONE);
    contracts.value = contractOptions;
    dropdownContract.value = contractOptions;
});
</script>
<template>
    <div>
        <ControlledDropdown
            :open="open"
            :fit-content="fitContent && !fullWidth"
            :full-width="fullWidth"
            :width="width"
            @change="handleControlledDropdownChange"
        >
            <template #trigger-zone>
                <ComboButton
                    :variant="variant"
                    :focus="open"
                    :icon-left="showIcon ? icon : undefined"
                    :placeholder="!modelValue"
                    :error="error"
                    :fullWidth="fullWidth"
                    @click="toggle"
                >
                    {{ modelValue?.label ?? placeholder ?? t("page.order.contract") }}
                </ComboButton>
            </template>
            <template #dropdown-content>
                <div class="contract-container">
                    <DropdownItem
                        v-for="contract of dropdownContract"
                        :key="contract.value"
                        @click="() => handleContractSelect(contract.value)"
                        :checked="modelValue?.value === contract.value"
                    >
                        {{ contract.label }}
                    </DropdownItem>
                </div>
            </template>
        </ControlledDropdown>
    </div>
</template>
<style scoped lang="scss">
.contract-container {
    padding-top: 4px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    overflow-y: auto;
}
</style>
